/**
 * Request Deduplication Utility
 * 
 * This utility prevents duplicate API requests by caching ongoing requests
 * and returning the same promise for identical requests.
 */

/**
 * Request cache to store ongoing requests
 */
const requestCache = new Map();

/**
 * Generate a cache key from request parameters
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} config - Request config
 * @returns {string} Cache key
 */
const generateCacheKey = (method, url, data = null, config = {}) => {
  // Create a stable key by sorting object properties
  const sortedData = data ? JSON.stringify(data, Object.keys(data).sort()) : '';
  const sortedConfig = JSON.stringify(
    {
      headers: config.headers || {},
      params: config.params || {}
    },
    Object.keys(config.headers || {}).concat(Object.keys(config.params || {})).sort()
  );
  
  return `${method.toUpperCase()}:${url}:${sortedData}:${sortedConfig}`;
};

/**
 * Deduplicated axios request wrapper
 * @param {Function} axiosInstance - Axios instance to use
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} config - Request config
 * @returns {Promise} Request promise
 */
export const deduplicatedRequest = async (axiosInstance, method, url, data = null, config = {}) => {
  const cacheKey = generateCacheKey(method, url, data, config);
  
  // Check if request is already in progress
  if (requestCache.has(cacheKey)) {
    console.log(`🔄 Deduplicating request: ${method.toUpperCase()} ${url}`);
    return requestCache.get(cacheKey);
  }
  
  // Create new request promise
  const requestPromise = (async () => {
    try {
      let response;
      
      switch (method.toLowerCase()) {
        case 'get':
          response = await axiosInstance.get(url, config);
          break;
        case 'post':
          response = await axiosInstance.post(url, data, config);
          break;
        case 'put':
          response = await axiosInstance.put(url, data, config);
          break;
        case 'patch':
          response = await axiosInstance.patch(url, data, config);
          break;
        case 'delete':
          response = await axiosInstance.delete(url, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
      
      return response;
    } finally {
      // Remove from cache when request completes (success or error)
      requestCache.delete(cacheKey);
    }
  })();
  
  // Cache the promise
  requestCache.set(cacheKey, requestPromise);
  
  return requestPromise;
};

/**
 * Create deduplicated axios methods
 * @param {Object} axiosInstance - Axios instance
 * @returns {Object} Deduplicated axios methods
 */
export const createDeduplicatedAxios = (axiosInstance) => {
  return {
    get: (url, config) => deduplicatedRequest(axiosInstance, 'get', url, null, config),
    post: (url, data, config) => deduplicatedRequest(axiosInstance, 'post', url, data, config),
    put: (url, data, config) => deduplicatedRequest(axiosInstance, 'put', url, data, config),
    patch: (url, data, config) => deduplicatedRequest(axiosInstance, 'patch', url, data, config),
    delete: (url, config) => deduplicatedRequest(axiosInstance, 'delete', url, null, config),
    
    // Original methods for when deduplication is not desired
    original: axiosInstance
  };
};

/**
 * Clear all cached requests (useful for cleanup)
 */
export const clearRequestCache = () => {
  requestCache.clear();
  console.log('🧹 Request cache cleared');
};

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  return {
    size: requestCache.size,
    keys: Array.from(requestCache.keys())
  };
};

/**
 * React hook for deduplicated requests
 * @param {Object} axiosInstance - Axios instance
 * @returns {Object} Deduplicated axios methods
 */
export const useDeduplicatedAxios = (axiosInstance) => {
  return createDeduplicatedAxios(axiosInstance);
};

/**
 * Configuration for request deduplication
 */
export const DEDUPLICATION_CONFIG = {
  // Methods that should be deduplicated (GET requests are safe to deduplicate)
  SAFE_METHODS: ['GET', 'HEAD', 'OPTIONS'],
  
  // Methods that should NOT be deduplicated (mutations should not be deduplicated)
  UNSAFE_METHODS: ['POST', 'PUT', 'PATCH', 'DELETE'],
  
  // Cache TTL for completed requests (to prevent immediate re-requests)
  CACHE_TTL: 1000, // 1 second
};

/**
 * Smart deduplication that only deduplicates safe methods
 * @param {Object} axiosInstance - Axios instance
 * @returns {Object} Smart deduplicated axios methods
 */
export const createSmartDeduplicatedAxios = (axiosInstance) => {
  const deduplicatedAxios = createDeduplicatedAxios(axiosInstance);
  
  return {
    get: deduplicatedAxios.get,
    head: (url, config) => deduplicatedRequest(axiosInstance, 'head', url, null, config),
    options: (url, config) => deduplicatedRequest(axiosInstance, 'options', url, null, config),
    
    // Don't deduplicate mutations
    post: axiosInstance.post.bind(axiosInstance),
    put: axiosInstance.put.bind(axiosInstance),
    patch: axiosInstance.patch.bind(axiosInstance),
    delete: axiosInstance.delete.bind(axiosInstance),
    
    // Utility methods
    clearCache: clearRequestCache,
    getCacheStats,
    original: axiosInstance
  };
};

export default {
  deduplicatedRequest,
  createDeduplicatedAxios,
  createSmartDeduplicatedAxios,
  clearRequestCache,
  getCacheStats,
  useDeduplicatedAxios,
  DEDUPLICATION_CONFIG
};
